import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AuditLogPage — P1-05 round 2.
 *
 * Read-only operator surface over GET /v1/admin/audit-log (P1-NF05). Lets an
 * operator slice the platform-wide event stream by app / actor / action /
 * time-window, paginate via opaque cursors, and inspect each event's full
 * details payload in a modal.
 *
 *   ┌─────────────────────────── filter bar ─────────────────────────────┐
 *   │ appId · actorUserId · action · since · until · limit  Apply Reset  │
 *   ├─────────────────────────────────────────────────────────────────────┤
 *   │  time      actor       app        action       target     details  │
 *   │  3m ago    Admin u#…   aquastar   app.meta.… …             View   │
 *   │  …                                                                  │
 *   └────────────────────────────────────── Showing N · Load more ───────┘
 *
 * Notes:
 *   - Pagination is cursor-based (infinite-append) — simpler than discrete
 *     page numbers and a near-exact mirror of the server's strict-`<`
 *     pagination contract.
 *   - actorUserId is validated client-side as a UUID before the request
 *     leaves; the server otherwise rejects bad UUIDs with Postgres 22P02
 *     bubbled up as a confusing 500. The "Invalid UUID" hint disables Apply.
 *   - `since`/`until` are `datetime-local` inputs (operator's local tz) and
 *     get converted to ISO Zulu on send. Quick-pick buttons fill the inputs
 *     so the operator can still tweak before applying.
 *   - A 1-second ticker re-renders the relative-time labels in the table so
 *     "3m ago" ages naturally without re-fetching (mirror of HealthDashboard).
 *   - Details modal is a plain Bootstrap-style overlay (no portal, no JS
 *     plugin) so admin-ui stays free of bootstrap-js as a runtime dep.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
const LIMIT_OPTIONS = [25, 50, 100, 250, 500];
const DEFAULT_LIMIT = 50;
const EMPTY_DRAFT = {
    appId: '',
    actorUserId: '',
    action: '',
    since: '',
    until: '',
    limit: String(DEFAULT_LIMIT),
};
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(s) {
    return UUID_RE.test(s.trim());
}
/**
 * Build the URL query string for /v1/admin/audit-log. Only includes keys
 * that are actually set so the server's default behavior kicks in for the
 * rest. Appending `cursor` is the caller's job (we keep the function pure).
 */
function buildQuery(applied, cursor) {
    const qs = new URLSearchParams();
    if (applied.appId)
        qs.set('appId', applied.appId);
    if (applied.actorUserId)
        qs.set('actorUserId', applied.actorUserId);
    if (applied.action)
        qs.set('action', applied.action);
    if (applied.since)
        qs.set('since', applied.since);
    if (applied.until)
        qs.set('until', applied.until);
    qs.set('limit', String(applied.limit));
    if (cursor)
        qs.set('cursor', cursor);
    return qs.toString();
}
/**
 * Convert the browser's datetime-local string ('YYYY-MM-DDTHH:mm' in the
 * operator's local tz) into an ISO Zulu timestamp the server can parse.
 * Returns null for empty input. Returns the raw string back if the Date
 * parse fails — the server's 400 will surface the bad input.
 */
function localToIso(local) {
    const trimmed = local.trim();
    if (!trimmed)
        return null;
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime()))
        return trimmed;
    return d.toISOString();
}
/**
 * Inverse of localToIso for prefilling quick-pick buttons. Produces the
 * 'YYYY-MM-DDTHH:mm' shape the datetime-local input expects, in local tz.
 */
function isoToLocal(d) {
    const pad = (n) => String(n).padStart(2, '0');
    return (`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}`);
}
function formatRelative(fromIso, now) {
    const then = Date.parse(fromIso);
    if (Number.isNaN(then))
        return fromIso;
    const deltaMs = now - then;
    if (deltaMs < 0)
        return 'just now';
    const sec = Math.floor(deltaMs / 1000);
    if (sec < 5)
        return 'just now';
    if (sec < 60)
        return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60)
        return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24)
        return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days === 1)
        return 'yesterday';
    if (days < 7)
        return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5)
        return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12)
        return `${months}mo ago`;
    const years = Math.floor(days / 365);
    return `${years}y ago`;
}
function formatAbsolute(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleString();
}
function truncateUuid(id) {
    if (id.length <= 12)
        return id;
    return `${id.slice(0, 8)}…${id.slice(-4)}`;
}
function actorBadgeColor(t) {
    switch (t) {
        case 'admin':
            return 'primary';
        case 'system':
            return 'secondary';
        case 'handler':
            return 'info';
    }
}
// ---------------------------------------------------------------------
// Component
export function AuditLogPage() {
    const fetcher = useNexusFetch();
    // Filter draft (the inputs) + applied filters (what's currently fetched).
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [applied, setApplied] = useState({
        limit: DEFAULT_LIMIT,
    });
    // Page state.
    const [events, setEvents] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [detailFor, setDetailFor] = useState(null);
    // Tick driver for relative-timestamp re-render.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);
    // ---- Validation derived from the draft ----------------------------
    const actorUserIdInvalid = draft.actorUserId.trim().length > 0 && !isUuid(draft.actorUserId);
    // ---- Fetch --------------------------------------------------------
    const fetchPage = useCallback(async (filters, pageCursor, mode) => {
        if (mode === 'replace')
            setLoading(true);
        else
            setLoadingMore(true);
        setError(null);
        try {
            const qs = buildQuery(filters, pageCursor);
            const res = await fetcher(`/v1/admin/audit-log${qs ? `?${qs}` : ''}`);
            const page = res?.events ?? [];
            const next = res?.nextCursor ?? null;
            setEvents((prev) => (mode === 'replace' ? page : [...prev, ...page]));
            setNextCursor(next);
        }
        catch (err) {
            const msg = err instanceof NexusAdminApiError
                ? `${err.code}: ${err.message}`
                : err instanceof Error
                    ? err.message
                    : 'Failed to load audit log';
            setError(msg);
            if (mode === 'replace') {
                setEvents([]);
                setNextCursor(null);
            }
        }
        finally {
            if (mode === 'replace')
                setLoading(false);
            else
                setLoadingMore(false);
        }
    }, [fetcher]);
    // Kick the initial load + reload whenever `applied` changes.
    useEffect(() => {
        setCursor(null);
        void fetchPage(applied, null, 'replace');
    }, [applied, fetchPage]);
    // ---- Filter-bar actions -------------------------------------------
    const onApply = useCallback((e) => {
        e.preventDefault();
        if (actorUserIdInvalid)
            return;
        const limitNum = Number(draft.limit);
        const safeLimit = Number.isFinite(limitNum) && limitNum >= 1 && limitNum <= 500
            ? Math.floor(limitNum)
            : DEFAULT_LIMIT;
        const next = { limit: safeLimit };
        if (draft.appId.trim())
            next.appId = draft.appId.trim();
        if (draft.actorUserId.trim())
            next.actorUserId = draft.actorUserId.trim();
        if (draft.action.trim())
            next.action = draft.action.trim();
        const sinceIso = localToIso(draft.since);
        const untilIso = localToIso(draft.until);
        if (sinceIso)
            next.since = sinceIso;
        if (untilIso)
            next.until = untilIso;
        setApplied(next);
    }, [draft, actorUserIdInvalid]);
    const onReset = useCallback(() => {
        setDraft(EMPTY_DRAFT);
        setApplied({ limit: DEFAULT_LIMIT });
    }, []);
    const onLoadMore = useCallback(() => {
        if (!nextCursor || loadingMore)
            return;
        setCursor(nextCursor);
        void fetchPage(applied, nextCursor, 'append');
    }, [applied, fetchPage, nextCursor, loadingMore]);
    const setDraftField = useCallback((key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
    }, []);
    // Quick-pick shortcuts fill the datetime inputs; the operator still
    // hits Apply, so they can tweak before submitting.
    const setQuickRange = useCallback((kind) => {
        const now = new Date();
        let from;
        let to;
        if (kind === 'today') {
            from = new Date(now);
            from.setHours(0, 0, 0, 0);
            to = now;
        }
        else if (kind === 'yesterday') {
            from = new Date(now);
            from.setDate(from.getDate() - 1);
            from.setHours(0, 0, 0, 0);
            to = new Date(from);
            to.setHours(23, 59, 0, 0);
        }
        else {
            from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            to = now;
        }
        setDraft((d) => ({ ...d, since: isoToLocal(from), until: isoToLocal(to) }));
    }, []);
    // ---- Derived ------------------------------------------------------
    const showing = events.length;
    const hasAnyFilter = useMemo(() => {
        return Boolean(applied.appId ||
            applied.actorUserId ||
            applied.action ||
            applied.since ||
            applied.until);
    }, [applied]);
    // ---- Render -------------------------------------------------------
    return (_jsxs("div", { children: [_jsx("div", { className: "d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2", children: _jsxs("div", { children: [_jsx("h1", { className: "h3 mb-1", children: "Audit log" }), _jsxs("p", { className: "text-muted small mb-0", children: ["Platform-wide event feed. Order: newest first. Pagination is strict ", _jsx("code", { children: "<" }), " on ", _jsx("code", { children: "createdAt" }), "."] })] }) }), _jsx("form", { className: "card mb-3", onSubmit: onApply, children: _jsxs("div", { className: "card-body", children: [_jsxs("div", { className: "row g-2", children: [_jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "audit-appid", children: "App ID" }), _jsx("input", { id: "audit-appid", type: "text", className: "form-control form-control-sm", value: draft.appId, onChange: (e) => setDraftField('appId', e.target.value), autoComplete: "off", spellCheck: false, placeholder: "e.g. aquastar" })] }), _jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "audit-actor", children: "Actor user ID" }), _jsx("input", { id: "audit-actor", type: "text", className: `form-control form-control-sm${actorUserIdInvalid ? ' is-invalid' : ''}`, value: draft.actorUserId, onChange: (e) => setDraftField('actorUserId', e.target.value), autoComplete: "off", spellCheck: false, placeholder: "UUID" }), actorUserIdInvalid && (_jsx("div", { className: "invalid-feedback d-block small", children: "Invalid UUID \u2014 request will not be sent." }))] }), _jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "audit-action", children: "Action" }), _jsx("input", { id: "audit-action", type: "text", className: "form-control form-control-sm", value: draft.action, onChange: (e) => setDraftField('action', e.target.value), autoComplete: "off", spellCheck: false, placeholder: "exact match" })] }), _jsxs("div", { className: "col-md-3", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "audit-limit", children: "Limit" }), _jsx("select", { id: "audit-limit", className: "form-select form-select-sm", value: draft.limit, onChange: (e) => setDraftField('limit', e.target.value), children: LIMIT_OPTIONS.map((n) => (_jsx("option", { value: n, children: n }, n))) })] }), _jsxs("div", { className: "col-md-4", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "audit-since", children: "Since" }), _jsx("input", { id: "audit-since", type: "datetime-local", className: "form-control form-control-sm", value: draft.since, onChange: (e) => setDraftField('since', e.target.value) })] }), _jsxs("div", { className: "col-md-4", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "audit-until", children: "Until" }), _jsx("input", { id: "audit-until", type: "datetime-local", className: "form-control form-control-sm", value: draft.until, onChange: (e) => setDraftField('until', e.target.value) })] }), _jsx("div", { className: "col-md-4 d-flex align-items-end", children: _jsxs("div", { className: "btn-group btn-group-sm", role: "group", "aria-label": "Quick ranges", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => setQuickRange('today'), children: "Today" }), _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => setQuickRange('yesterday'), children: "Yesterday" }), _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => setQuickRange('7d'), children: "7 days" })] }) })] }), _jsxs("div", { className: "d-flex gap-2 mt-3", children: [_jsx("button", { type: "submit", className: "btn btn-primary btn-sm", disabled: actorUserIdInvalid || loading, children: loading ? 'Applying…' : 'Apply filters' }), _jsx("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: onReset, disabled: loading, children: "Reset" })] })] }) }), _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-body p-0", children: [loading && events.length === 0 && (_jsxs("div", { className: "d-flex align-items-center gap-2 text-muted p-3", children: [_jsx("div", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" }), _jsx("span", { children: "Loading audit log\u2026" })] })), error && (_jsx("div", { className: "alert alert-danger m-3 mb-0", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsx("span", { children: error }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void fetchPage(applied, cursor, 'replace'), children: "Retry" })] }) })), !loading && !error && events.length === 0 && (_jsxs("div", { className: "text-center text-muted p-4", children: [_jsxs("p", { className: "mb-1", children: ["No audit events found", hasAnyFilter ? ' for the current filters' : '', "."] }), !hasAnyFilter && (_jsxs("p", { className: "small mb-0", children: ["This becomes populated once admin handlers + system actors start calling ", _jsx("code", { children: "auditLog.record()" }), " \u2014 see follow-up P-NF entries."] }))] })), events.length > 0 && (_jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-striped table-sm mb-0 align-middle", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", style: { minWidth: '8rem' }, children: "Time" }), _jsx("th", { scope: "col", children: "Actor" }), _jsx("th", { scope: "col", children: "App" }), _jsx("th", { scope: "col", children: "Action" }), _jsx("th", { scope: "col", children: "Target" }), _jsx("th", { scope: "col", className: "text-end", children: "Details" })] }) }), _jsx("tbody", { children: events.map((ev) => (_jsx(EventRow, { event: ev, now: now, onOpenDetails: () => setDetailFor(ev) }, ev.id))) })] }) }))] }), (events.length > 0 || nextCursor) && (_jsxs("div", { className: "card-footer d-flex justify-content-between align-items-center small", children: [_jsxs("span", { className: "text-muted", children: ["Showing ", _jsx("span", { className: "fw-semibold", children: showing }), ' ', "event", showing === 1 ? '' : 's', nextCursor && _jsx("span", { className: "ms-1", children: "(more available)" })] }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-primary", onClick: onLoadMore, disabled: !nextCursor || loadingMore || loading, children: loadingMore ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Loading\u2026"] })) : nextCursor ? ('Load more') : ('End of feed') })] }))] }), detailFor && (_jsx(DetailsModal, { event: detailFor, onClose: () => setDetailFor(null) }))] }));
}
function EventRow({ event: ev, now, onOpenDetails }) {
    const relative = formatRelative(ev.createdAt, now);
    const absolute = formatAbsolute(ev.createdAt);
    return (_jsxs("tr", { children: [_jsx("td", { children: _jsx("span", { title: absolute, children: relative }) }), _jsxs("td", { children: [_jsx("span", { className: `badge text-bg-${actorBadgeColor(ev.actorType)} me-1`, children: ev.actorType }), ev.actorUserId ? (_jsx("code", { className: "small", title: ev.actorUserId, children: truncateUuid(ev.actorUserId) })) : (_jsx("span", { className: "text-muted small", children: "\u2014" }))] }), _jsx("td", { children: ev.appId ? (_jsx("span", { className: "badge text-bg-light border", children: ev.appId })) : (_jsx("span", { className: "text-muted small", children: "(platform)" })) }), _jsx("td", { children: _jsx("code", { children: ev.action }) }), _jsx("td", { children: ev.targetType || ev.targetId ? (_jsxs("code", { className: "small", children: [ev.targetType ?? '?', ev.targetId ? `:${ev.targetId}` : ''] })) : (_jsx("span", { className: "text-muted small", children: "\u2014" })) }), _jsx("td", { className: "text-end", children: _jsx("button", { type: "button", className: "btn btn-sm btn-link p-0", onClick: onOpenDetails, children: "View" }) })] }));
}
function DetailsModal({ event: ev, onClose }) {
    // Close on Escape — small ergonomics win.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    const detailsJson = useMemo(() => {
        try {
            return JSON.stringify(ev.details ?? {}, null, 2);
        }
        catch {
            return String(ev.details);
        }
    }, [ev.details]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal d-block", role: "dialog", "aria-modal": "true", tabIndex: -1, onClick: onClose, children: _jsx("div", { className: "modal-dialog modal-lg modal-dialog-scrollable", onClick: (e) => e.stopPropagation(), children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: _jsx("code", { children: ev.action }) }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onClose })] }), _jsxs("div", { className: "modal-body", children: [_jsxs("dl", { className: "row mb-3 small", children: [_jsx("dt", { className: "col-sm-3 fw-normal text-muted", children: "Event ID" }), _jsx("dd", { className: "col-sm-9", children: _jsx("code", { children: ev.id }) }), _jsx("dt", { className: "col-sm-3 fw-normal text-muted", children: "Time" }), _jsxs("dd", { className: "col-sm-9", children: [formatAbsolute(ev.createdAt), ' ', _jsxs("span", { className: "text-muted", children: ["(", ev.createdAt, ")"] })] }), _jsx("dt", { className: "col-sm-3 fw-normal text-muted", children: "Actor" }), _jsxs("dd", { className: "col-sm-9", children: [_jsx("span", { className: `badge text-bg-${actorBadgeColor(ev.actorType)} me-2`, children: ev.actorType }), ev.actorUserId ? (_jsx("code", { children: ev.actorUserId })) : (_jsx("span", { className: "text-muted", children: "\u2014" }))] }), _jsx("dt", { className: "col-sm-3 fw-normal text-muted", children: "App" }), _jsx("dd", { className: "col-sm-9", children: ev.appId ? (_jsx("code", { children: ev.appId })) : (_jsx("span", { className: "text-muted", children: "(platform)" })) }), _jsx("dt", { className: "col-sm-3 fw-normal text-muted", children: "Target" }), _jsx("dd", { className: "col-sm-9", children: ev.targetType || ev.targetId ? (_jsxs("code", { children: [ev.targetType ?? '?', ev.targetId ? `:${ev.targetId}` : ''] })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) }), _jsx("dt", { className: "col-sm-3 fw-normal text-muted", children: "IP address" }), _jsx("dd", { className: "col-sm-9", children: ev.ipAddress ? (_jsx("code", { children: ev.ipAddress })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) }), _jsx("dt", { className: "col-sm-3 fw-normal text-muted", children: "User agent" }), _jsx("dd", { className: "col-sm-9", children: ev.userAgent ? (_jsx("span", { className: "small text-break", children: ev.userAgent })) : (_jsx("span", { className: "text-muted", children: "\u2014" })) })] }), _jsx("h6", { className: "text-muted small text-uppercase mb-1", children: "Details" }), _jsx("pre", { className: "bg-light border rounded p-2 small mb-0", style: { maxHeight: '24rem', overflow: 'auto' }, children: detailsJson })] }), _jsx("div", { className: "modal-footer", children: _jsx("button", { type: "button", className: "btn btn-secondary", onClick: onClose, children: "Close" }) })] }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
//# sourceMappingURL=AuditLogPage.js.map